export interface LessonConceptCheck {
  id: string;
  prompt: string;
  choices: string[];
  answerIndex: number;
  explanation: string;
}

export interface LessonSection {
  id: string;
  title: string;
  headline: string;
  summary: string;
  bigIdeas: string[];
  actionSteps: string[];
  conceptCheck: LessonConceptCheck;
  reference: string;
}

export const geometryLessonSections: LessonSection[] = [
  {
    id: 'cracking-the-system',
    title: 'Cracking the System',
    headline: 'Treat the GRE like a game you can learn to play.',
    summary:
      'The PDF opens by explaining that the GRE rewards familiarity with the test makers’ mindset. Success comes from practicing ETS-style logic, not from mastering every concept.',
    bigIdeas: [
      'The GRE measures how well you take the GRE—strategy matters as much as content.',
      'Thinking like ETS means anticipating distractors and spotting point-rich shortcuts.',
      'Confidence grows when you reframe the exam as a predictable system instead of a mystery.'
    ],
    actionSteps: [
      'Before drilling math, skim a few ETS-style explanations and note how they simplify work.',
      'When reviewing a miss, log the specific “trap” you fell for so you can recognize it later.',
      'Plan practice blocks that mix strategy review with problem sets to reinforce both.'
    ],
    conceptCheck: {
      id: 'cracking-check',
      prompt: 'According to the learning guide, what is the first mindset shift for boosting your GRE score?',
      choices: [
        'Memorize every geometry formula before touching practice questions.',
        'Realize you can substantially improve by practicing ETS-style strategies.',
        'Focus only on content review because the test is coach-proof.',
        'Let the test writers intimidate you so you take each question more seriously.'
      ],
      answerIndex: 1,
      explanation:
        'The PDF stresses that the GRE rewards people who believe it is coachable and who study the test maker’s patterns. Memorization alone is not the starting point.'
    },
    reference: 'GRE Math Review – Geometry PDF, intro pages'
  },
  {
    id: 'two-pass-approach',
    title: 'Two-Pass Time Management',
    headline: 'Skip early, come back later, and protect your accuracy.',
    summary:
      'The “Take the Easy Test First” and “Mark and Return” sections outline a two-pass system so you capture quick points before spending time on grindy geometry setups.',
    bigIdeas: [
      'Every question is worth the same; prioritize the ones you can solve in under a minute.',
      'Use the Mark feature to reset your brain whenever a diagram or wording feels off.',
      'Walking away from a tough problem gives your working memory a chance to reset.'
    ],
    actionSteps: [
      'During drills, practice cycling through an entire set marking anything longer than 60 seconds.',
      'Force yourself to leave any geometry question that stalls for 30 seconds and revisit after two others.',
      'Log how many problems you solved on pass one versus pass two to refine pacing.'
    ],
    conceptCheck: {
      id: 'two-pass-check',
      prompt: 'How does the guide recommend handling a geometry question that suddenly feels confusing?',
      choices: [
        'Double your speed so you finish it before the confusion grows.',
        'Stay until you solve it, even if it takes five minutes, to avoid extra passes.',
        'Mark it, walk away to other items, then return with a reset mindset.',
        'Guess immediately because marked questions cannot be revisited.'
      ],
      answerIndex: 2,
      explanation:
        'Mark-and-return is highlighted as the way to protect time while giving your brain a chance to reset and re-read the prompt accurately.'
    },
    reference: 'GRE Math Review – Geometry PDF, “General Strategies” section'
  },
  {
    id: 'poe-and-guessing',
    title: 'Process of Elimination & Smart Guessing',
    headline: 'Eliminate aggressively—ETS already gave you the answers.',
    summary:
      'The PDF emphasizes that wrong choices (“distractors”) are engineered to tempt you. Eliminating them is often faster than grinding through full calculations.',
    bigIdeas: [
      'Every removed option increases the odds that a guess will land on ETS’s credited answer.',
      'Geometry problems often hide size or parity clues that make extreme answers impossible.',
      'Guessing after eliminating choices carries zero penalty, so unanswered questions are the real danger.'
    ],
    actionSteps: [
      'While reviewing solutions, note at least one reason each wrong choice is impossible.',
      'Practice back-solving by plugging answer choices into diagrams to see which violate constraints.',
      'In the final two minutes, ensure every blank is filled after a rapid POE sweep.'
    ],
    conceptCheck: {
      id: 'poe-check',
      prompt: 'Why does the learning guide insist on POE for tough geometry problems?',
      choices: [
        'Because ETS awards partial credit for eliminating answers.',
        'Because eliminating even one distractor improves the odds of a correct guess.',
        'Because geometry questions never require actual computation.',
        'Because POE is only useful on vocabulary questions, not math.'
      ],
      answerIndex: 1,
      explanation:
        'The text highlights that each removed distractor boosts your probability of guessing right, which is crucial when exact computation would be slow.'
    },
    reference: 'GRE Math Review – Geometry PDF, “Process of Elimination” pages'
  },
  {
    id: 'scratch-paper-discipline',
    title: 'Scratch Paper Discipline',
    headline: 'Get math out of your head and onto the page.',
    summary:
      'Later pages remind you that ETS gives only a few scratch sheets, but using them well prevents careless mistakes during multi-step geometry work.',
    bigIdeas: [
      'Mistakes happen when you try to compute and remember steps simultaneously.',
      'Structured work areas (diagrams, givens, goal) reduce transcription errors.',
      'Re-reading the question after writing the setup guards against solving for the wrong value.'
    ],
    actionSteps: [
      'Adopt a consistent scratch layout: diagram, known values, target, then work.',
      'Practice re-copying key measurements before solving so you catch misreads early.',
      'Create a mini “double-check” checklist (units, what’s asked, extreme cases) for every official problem.'
    ],
    conceptCheck: {
      id: 'scratch-check',
      prompt: 'What is the main reason the guide gives for offloading work to scratch paper?',
      choices: [
        'Scratch paper is optional, so using it earns bonus points.',
        'Doing two tasks in your head at once increases careless errors during geometry calculations.',
        'ETS allows you to submit scratch paper for partial credit.',
        'Writing on scratch paper lets you skip reading the problem twice.'
      ],
      answerIndex: 1,
      explanation:
        'The PDF states that mistakes occur when you both track steps and compute mentally; structured scratch work keeps thinking organized.'
    },
    reference: 'GRE Math Review – Geometry PDF, “Use Your Scratch Paper”'
  }
];

